/**
 * Shared primitives for both LocalSqliteEditor (attach) and
 * ConnectionEditModal (reattach). Keep this module deps-free of React
 * — pure functions over `worker`, `handle`, and IDB.
 */
import { TaskType, type SqliteAttachToOpfsResult } from "Worker/entities";
import { requestFromWorker } from "Worker/workerRequest";
import { collectEvictionCandidates } from "stores/sqliteHandles";

const SQLITE_PICKER_TYPES = [
  {
    description: "SQLite database",
    accept: {
      "application/vnd.sqlite3": [".sqlite", ".db", ".sqlite3"],
      "application/x-sqlite3": [".sqlite", ".db", ".sqlite3"],
    },
  },
];

export type PickResult =
  | { kind: "picked"; handle: FileSystemFileHandle }
  | { kind: "cancelled" }
  | { kind: "unsupported" };

/** User-gesture file pick via FSA's `showOpenFilePicker`. Returns a
 *  discriminated result so callers don't have to re-derive AbortError
 *  vs missing-API. */
export async function pickSqliteFile(): Promise<PickResult> {
  const w = window as unknown as {
    showOpenFilePicker?: (opts: {
      types?: typeof SQLITE_PICKER_TYPES;
      multiple?: boolean;
    }) => Promise<FileSystemFileHandle[]>;
  };
  if (!w.showOpenFilePicker) return { kind: "unsupported" };
  try {
    const [handle] = await w.showOpenFilePicker({
      multiple: false,
      types: SQLITE_PICKER_TYPES,
    });
    return handle ? { kind: "picked", handle } : { kind: "cancelled" };
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      return { kind: "cancelled" };
    }
    throw e;
  }
}

/** Post `SqliteAttachToOpfs` to the worker with the LRU eviction
 *  candidates pre-collected. Throws if the worker is not ready. */
export async function attachSqliteToOpfsViaWorker(
  worker: Worker | null,
  handle: FileSystemFileHandle,
): Promise<SqliteAttachToOpfsResult> {
  if (!worker) {
    throw new Error("Worker not ready. Reload the page and try again.");
  }
  const evictionCandidates = await collectEvictionCandidates();
  return requestFromWorker<SqliteAttachToOpfsResult>(
    worker,
    TaskType.SqliteAttachToOpfs,
    { handle, evictionCandidates },
  );
}
